<template>
  <div class="container mx-auto px-4 py-8">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-8">
        <h1 class="text-4xl font-bold text-slate-100 mb-4">🎨 Mermaid Diagram Converter</h1>
        <p class="text-lg text-slate-400">Convert your Mermaid diagrams to PNG, JPG, SVG, or PDF</p>
      </div>

      <!-- Main Content -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Input Panel -->
        <div class="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6">
          <h2 class="text-2xl font-semibold text-slate-100 mb-4">📝 Mermaid Markdown Input</h2>
          
          <textarea
            v-model="mermaidInput"
            class="w-full h-80 p-4 bg-slate-900 border border-slate-700 rounded-lg font-mono text-sm resize-vertical focus:border-blue-500 focus:outline-none transition-colors text-slate-200"
            placeholder="Enter your Mermaid diagram here...

Example:
graph TD
    A[Start] --> B{Decision}
    B -->|Yes| C[Process 1]
    B -->|No| D[Process 2]
    C --> E[End]
    D --> E"
          ></textarea>
          
          <div class="flex gap-3 mt-4">
            <button
              @click="renderDiagram"
              class="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transform hover:-translate-y-1 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              🔄 Render Diagram
            </button>
            <button
              @click="clearAll"
              class="flex-1 bg-slate-700 text-slate-200 px-6 py-3 rounded-lg font-semibold hover:bg-slate-600 transition-colors"
            >
              🗑️ Clear
            </button>
          </div>

          <!-- Status Message -->
          <div
            v-if="statusMessage"
            :class="[
              'mt-4 p-3 rounded-lg text-center text-sm font-medium',
              statusType === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            ]"
          >
            {{ statusMessage }}
          </div>
        </div>

        <!-- Preview Panel -->
        <div class="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6">
          <h2 class="text-2xl font-semibold text-slate-100 mb-4">👁️ Preview & Export</h2>
          
          <div class="border border-slate-700 rounded-lg p-5 min-h-80 bg-slate-900 flex items-center justify-center overflow-auto">
            <div v-html="previewContent" class="max-w-full"></div>
          </div>

          <div class="grid grid-cols-2 gap-3 mt-4">
            <button
              @click="exportAs('png')"
              class="bg-slate-800 border border-blue-500 text-blue-400 px-4 py-3 rounded-lg font-semibold hover:bg-blue-500 hover:text-white transform hover:-translate-y-1 transition-all duration-200"
            >
              📥 Download PNG
            </button>
            <button
              @click="exportAs('jpg')"
              class="bg-slate-800 border border-blue-500 text-blue-400 px-4 py-3 rounded-lg font-semibold hover:bg-blue-500 hover:text-white transform hover:-translate-y-1 transition-all duration-200"
            >
              📥 Download JPG
            </button>
            <button
              @click="exportAs('svg')"
              class="bg-slate-800 border border-blue-500 text-blue-400 px-4 py-3 rounded-lg font-semibold hover:bg-blue-500 hover:text-white transform hover:-translate-y-1 transition-all duration-200"
            >
              📥 Download SVG
            </button>
            <!-- <button
              @click="exportAs('pdf')"
              class="bg-white border-2 border-blue-500 text-blue-500 px-4 py-3 rounded-lg font-semibold hover:bg-blue-500 hover:text-white transform hover:-translate-y-1 transition-all duration-200"
            >
              📥 Download PDF
            </button> -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';

export default defineComponent({
  name: 'MermaidConverter',
  setup() {
    const mermaidInput = ref(`graph TD
    A[Start] --> B{Decision}
    B -->|Yes| C[Process 1]
    B -->|No| D[Process 2]
    C --> E[End]
    D --> E`);
    
    const previewContent = ref('');
    const statusMessage = ref('');
    const statusType = ref<'success' | 'error'>('success');
    const currentSvg = ref<string | null>(null);

    onMounted(async () => {
      // Load Mermaid from CDN
      if (typeof window !== 'undefined' && !(window as any).mermaid) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/mermaid/10.6.1/mermaid.min.js';
        script.onload = () => {
          (window as any).mermaid.initialize({ 
            startOnLoad: false,
            theme: 'default',
            securityLevel: 'loose'
          });
          renderDiagram();
        };
        document.head.appendChild(script);
      } else if ((window as any).mermaid) {
        (window as any).mermaid.initialize({ 
          startOnLoad: false,
          theme: 'default',
          securityLevel: 'loose'
        });
        renderDiagram();
      }
    });

    const showStatus = (message: string, type: 'success' | 'error') => {
      statusMessage.value = message;
      statusType.value = type;
      setTimeout(() => {
        statusMessage.value = '';
      }, 3000);
    };

    const renderDiagram = async () => {
      const input = mermaidInput.value.trim();

      if (!input) {
        showStatus('Please enter Mermaid diagram code', 'error');
        return;
      }

      if (!(window as any).mermaid) {
        showStatus('Mermaid is still loading, please wait...', 'error');
        return;
      }

      try {
        previewContent.value = '';
        const id = 'mermaid-' + Date.now();
        const { svg } = await (window as any).mermaid.render(id, input);
        previewContent.value = svg;
        currentSvg.value = svg;
        showStatus('Diagram rendered successfully!', 'success');
      } catch (error: any) {
        showStatus('Error rendering diagram: ' + error.message, 'error');
        previewContent.value = '<p style="color: #721c24;">Invalid Mermaid syntax. Please check your code.</p>';
      }
    };

    const clearAll = () => {
      mermaidInput.value = '';
      previewContent.value = '';
      currentSvg.value = null;
      showStatus('Cleared!', 'success');
    };

    const exportAs = async (format: string) => {
      if (!currentSvg.value) {
        showStatus('Please render a diagram first', 'error');
        return;
      }

      try {
        if (format === 'svg') {
          downloadFile(currentSvg.value, 'diagram.svg', 'image/svg+xml');
          showStatus('SVG downloaded!', 'success');
          return;
        }

        const svgElement = document.querySelector('.max-w-full svg') as SVGSVGElement;
        if (!svgElement) {
          showStatus('No diagram to export', 'error');
          return;
        }

        const svgClone = svgElement.cloneNode(true) as SVGSVGElement;
        const bbox = svgElement.getBBox();
        const padding = 40;
        const width = bbox.width + padding;
        const height = bbox.height + padding;
        
        svgClone.setAttribute('width', width.toString());
        svgClone.setAttribute('height', height.toString());
        svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        
        // Get all styles from the document
        const styles = Array.from(document.styleSheets)
          .filter(sheet => {
            try {
              return sheet.cssRules;
            } catch (e) {
              return false;
            }
          })
          .reduce((acc, sheet) => {
            return acc + Array.from(sheet.cssRules)
              .map(rule => rule.cssText)
              .join('\n');
          }, '');

        // Add inline styles to SVG
        const styleElement = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        styleElement.textContent = styles;
        svgClone.insertBefore(styleElement, svgClone.firstChild);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const scale = 2;
        
        canvas.width = width * scale;
        canvas.height = height * scale;
        ctx!.scale(scale, scale);

        if (format === 'jpg') {
          ctx!.fillStyle = 'white';
          ctx!.fillRect(0, 0, canvas.width, canvas.height);
        }

        const svgString = new XMLSerializer().serializeToString(svgClone);
        const svgBase64 = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));

        const img = new Image();
        
        img.onload = function() {
          ctx!.drawImage(img, 0, 0, width, height);

          if (format === 'pdf') {
            canvas.toBlob(function(blob) {
              downloadFile(blob!, 'diagram.pdf', 'application/pdf');
              showStatus('PDF downloaded!', 'success');
            }, 'image/png');
          } else {
            const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
            const quality = format === 'jpg' ? 0.95 : undefined;
            canvas.toBlob(function(blob) {
              if (blob) {
                downloadFile(blob, `diagram.${format}`, mimeType);
                showStatus(`${format.toUpperCase()} downloaded!`, 'success');
              } else {
                showStatus('Error creating image', 'error');
              }
            }, mimeType, quality);
          }
        };

        img.onerror = function(e) {
          showStatus('Error loading image for export. Try SVG export instead.', 'error');
          console.error('Image load error:', e);
        };

        img.src = svgBase64;
      } catch (error: any) {
        showStatus('Error exporting: ' + error.message, 'error');
        console.error('Export error:', error);
      }
    };

    const downloadFile = (content: string | Blob, filename: string, mimeType: string) => {
      const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    return {
      mermaidInput,
      previewContent,
      statusMessage,
      statusType,
      renderDiagram,
      clearAll,
      exportAs
    };
  }
});
</script>

<style scoped>
/* Additional custom styles if needed */
</style>
